import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface ChatNotification {
  id: string;
  senderName: string;
  channel: string;      // "family" or doc ID
  channelLabel: string; // "Family" or meal name
  timestamp: string;    // ISO string
}

interface NotificationsContextValue {
  notifications: ChatNotification[];
  addNotification: (n: Omit<ChatNotification, "id">) => void;
  clearChannel: (channel: string) => void;
  clearAll: () => void;
  count: number;
}

const NotificationsContext = createContext<NotificationsContextValue>({
  notifications: [],
  addNotification: () => {},
  clearChannel: () => {},
  clearAll: () => {},
  count: 0,
});

export function useNotifications() {
  return useContext(NotificationsContext);
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);

  const addNotification = useCallback((n: Omit<ChatNotification, "id">) => {
    setNotifications((prev) => {
      // Don't duplicate: same sender + same channel
      const exists = prev.some(
        (p) => p.senderName === n.senderName && p.channel === n.channel
      );
      if (exists) {
        // Update the timestamp on the existing one
        return prev.map((p) =>
          p.senderName === n.senderName && p.channel === n.channel
            ? { ...p, timestamp: n.timestamp }
            : p
        );
      }
      return [...prev, { ...n, id: `${n.channel}-${n.senderName}-${Date.now()}` }];
    });
  }, []);

  const clearChannel = useCallback((channel: string) => {
    setNotifications((prev) => prev.filter((n) => n.channel !== channel));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationsContext.Provider
      value={{ notifications, addNotification, clearChannel, clearAll, count: notifications.length }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}
